import {
    APIStatusType,
    ApiResultType,
    BankCode,
    ChargePointPolicyType,
    FinancialInvestmentCompanyCode,
    PAYMONEY_RESULT_TYPE,
    RechargePointLimitTypeType,
} from '@web/core/constants'

export const AccumulateState = {
    NORMAL: 'NORMAL',
    MAX: 'MAX',
    NONE: 'NONE',
} as const

export type AccumulateStateType = Constant<typeof AccumulateState>

/** 충전결제 사용 결제적립률이 변경되는 케이스 */
export const AccumulateRateBenefitCase = {
    /** 최대 적립률 */
    MAX: 'MAX',
    /** 페이머니 회원 */
    PAYMONEY_USER: 'PAYMONEY_USER',
    /** 네이버 제휴통장 사용한 충전 */
    NAVER_PARTNER_BANK_BOOK: 'NAVER_PARTNER_BANK_BOOK',
    /** 기본 적립률 */
    DEFAULT: 'DEFAULT',
} as const

export interface ChargePointState {
    /** 충전 포인트 잔액 */
    amount: number
    /** 실제 충전포인트 잔액 */
    actualAmount: number
    /** 비현금성 머니 잔액 */
    nonCashMoneyAmount: number
    /** 충전포인트 조회 상태 */
    status: APIStatusType
    /** 충전포인트 API 조회 결과 */
    apiResult: ApiResultType
    /** 재충전 가능한 충전 포인트 */
    reloadable: {
        /** 잔여 충전 가능 잔액 */
        amount: number
        /** 충전포인트 충전 상태 제한 요인 */
        limitedType: Maybe<RechargePointLimitTypeType>
        /** 충전한도 API 결과 타입 */
        apiResult: ApiResultType
        /** 충전 제한 금액 */
        limit: Maybe<number>
    }
    /** 충전할 충전 포인트 */
    recharging: {
        /** 충전할 포인트 */
        amount: number
        /**
         * 충전 적립 상태
         * - 단건결제는 아이템 각각마다 적립률 이 적용후 내림되어, 사용자가 인식하기에에 달라질 수 있어, 무조건 최대 부여
         * - 충전 적립이 아예 없는 상품은 none 선택
         * */
        accumulateStatus: AccumulateStateType
        /**
         * @deprecated 모든 앱에서 적립률 조회 API의 응답을 사용하게 되면, expectedAccumulateRate 사용
         * 케이스별 충전결제 적립률. 가맹점별 적립률이 다른 케이스에만 재정의된다.
         */
        accumulateRate: Record<Constant<typeof AccumulateRateBenefitCase>, number>
        /**
         * 충전 방법 설정시 충전 처리 방법. 자동/반복결제 페이지에서는 충전이 이후에 이루어지고 즉시 결제하는 페이지에서는 충전과 함께 결제가 이루어진다.
         */
        processingType: 'DEFERRED' | 'IMMEDIATE'
        /**
         * 충전결제 예상 적립률
         */
        expectedAccumulateRate?: number
        /**
         * 충전결제 예상 적립금
         */
        expectedReward?: number
        /** 충전수단의 pgId */
        pgId?: string
    }
    /** 페이머니 */
    payMoney: {
        /** 페이머니 통장 연결 은행코드 */
        bankCode: PaymoneyMemberTypeType
        /** 페이머니 통장 연결 은행명 */
        bankName: PaymoneyDisplayBankName
        /** 페이머니 회원 상태 */
        userStatus: PayMoneyUserStatusType
        /** 페이머니 사용자 상태 조회 API 결과 타입 */
        apiResult: Constant<typeof PAYMONEY_RESULT_TYPE>
        /** 페이머니 계좌 */
        maskedAccount: string
    }
    /**
     * 충전결제를 복합 결제로 진행하기 위해 필요한 필수 요소
     * CHARGE_ACCOUNT: 충전계좌가 없으면 충전결제 사용불가
     * PAY_MONEY_BALANCE: 페이머니회원의 머니 잔액만 있으면 사용가능
     */
    requiredMethod: 'CHARGE_ACCOUNT' | 'PAY_MONEY_BALANCE'
    /**
     * 충전포인트 사용정책
     * DEFAULT: 기본정책 > 페이머니 회원상태에 맞게 충전포인트 또는 페이머니로 취급
     * PAYMONEY: 항상 페이머니로 취급하여 결제수단 > 충전결제에서 관리)
     */
    policy: ChargePointPolicyType
    /** 포인트 최대 보유한도 금액
     * - AML 타겟인지 여부에 따라 변하는 고정 상수값
     * */
    maximumHoldingPoint: number
    /** 페이머니/충전결제로 결제가능한 최대 금액 */
    maximumUsingAmount: Maybe<number>
}

export type RootState = {chargePoint: ChargePointState}

/** 페이머니 제휴통장 타입 */
export const PaymoneyMemberType = {
    /** 페이머니 미가입자 */
    DEFAULT: '000',
    /** 페이머니 하나 */
    HANA: '081',
    /** 페이머니 미래에셋 */
    MIRAEASSET: '238',
    /** 페이머니 우리 */
    WOORI: '020',
} as const

/** 페이머니 제휴통장 타입 */
export type PaymoneyMemberTypeType = Constant<typeof PaymoneyMemberType>

/**
 * 페이머니 통장 display 은행명 map
 * (제휴사가 아닌 은행도 포함되어 있음)
 * @see https://oss.fin.navercorp.com/Product-Planning/Online-Payment_Plan/issues/574#issuecomment-1918960
 */
export const PaymoneyDisplayBankNameMap = {
    [PaymoneyMemberType.DEFAULT]: '',
    [BankCode.KDB]: 'KDB산업',
    [BankCode.IBK]: 'IBK기업',
    [BankCode.KB_STAR]: 'KB국민',
    [BankCode.SUHYUP_BANK]: '수협',
    [BankCode.NONG_HYUB]: 'NH농협',
    [BankCode.WOORI_BANK]: '우리',
    [BankCode.STANDARD_CHATERED]: 'SC제일',
    [BankCode.CITI_BANK]: '씨티',
    [BankCode.DGB]: 'iM뱅크',
    [BankCode.BUSAN_BANK]: '부산',
    [BankCode.KJ_BANK]: '광주',
    [BankCode.JEJU_BANK]: '제주',
    [BankCode.JB_BANK]: '전북',
    [BankCode.KB_BANK]: '경남',
    [BankCode.KFCC]: '새마을금고',
    [BankCode.CU]: '신협',
    [BankCode.FSB]: '저축',
    [BankCode.EPOST_BANK]: '우체국',
    [BankCode.HANA_BANK]: '하나',
    [BankCode.SHINHAN]: '신한',
    [BankCode.K_BANK]: '케이뱅크',
    [BankCode.KAKAO]: '카카오뱅크',
    [BankCode.FSBSBISB]: 'SBI저축',
    [FinancialInvestmentCompanyCode.YUANTA]: '유안타',
    [FinancialInvestmentCompanyCode.KBSEC]: 'KB투자',
    [FinancialInvestmentCompanyCode.MIRAEASSET]: '미래에셋',
    [FinancialInvestmentCompanyCode.SAMSUNG]: '삼성',
    [FinancialInvestmentCompanyCode.KOREA]: '한국투자',
    [FinancialInvestmentCompanyCode.SK]: 'SK',
    [FinancialInvestmentCompanyCode.EUGENEFN]: '유진투자',
    [BankCode.TOSS]: '토스뱅크',
    [FinancialInvestmentCompanyCode.SHINHANINVEST]: '신한투자',
    [FinancialInvestmentCompanyCode.NHQV]: 'NH투자',
} as const

/** 페이머니 제휴통장 displayName */
export type PaymoneyDisplayBankName = Pick<
    typeof PaymoneyDisplayBankNameMap,
    PaymoneyMemberTypeType
>[PaymoneyMemberTypeType]
